/**
 * Business hours — Step 31.
 *
 * The calendar an SLA clock runs inside. Everything here exists because
 * "respond within 4 hours" means four WORKING hours: a ticket raised at 17:30
 * Friday is not late at 21:30 Friday, and a tool that says otherwise trains
 * people to ignore it.
 *
 * Days are STORED index 0 = Sunday, matching `slaMath`'s
 * `BusinessCalendar.week` and `Date.getDay()`. They are DISPLAYED Monday
 * first, because that is how a UK week reads. `DISPLAY_ORDER` is the only
 * place the two differ.
 *
 * Holidays are entered as plain dates and are closed ALL day. Deliberately
 * not a recurring rule: "the Monday after the last Sunday in May" is how bank
 * holiday logic becomes a bug nobody can read. A list of dates is boring,
 * correct, and easy to check against reality.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Plus, Trash2 } from 'lucide-react';
import { apiFetch, apiSend } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { qk } from '../../lib/queryClient';
import { useUrlState } from '../../lib/urlState';
import { useDirty } from '../../lib/dirty';
import { useConfirm } from '../../lib/confirm';
import { toast } from 'sonner';

interface Shift {
  start: string;
  end: string;
}
interface Calendar {
  id: string;
  name: string;
  description: string | null;
  timezone: string;
  week: Shift[][];
  holidays: string[];
  isDefault: number;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * DISPLAY order — Monday first (Kai: "days of the week are Mon - Sun").
 *
 * STORAGE stays index 0 = Sunday, because that is `Date.getDay()` and it is
 * what `slaMath` indexes `BusinessCalendar.week` by. Reordering the array to
 * match the display would mean every calendar silently shifting a day, so the
 * two are kept apart on purpose: this list is the only place the order
 * differs, and it maps back through the stored index.
 */
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

/** A short list beats a full IANA dump: these are the zones a UK-based
 *  white-label desk actually serves. Any other zone can be typed. */
const ZONES = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Paris',
  'Europe/Berlin',
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'America/Guatemala',
  'Australia/Sydney',
];

const api = {
  list: () => apiFetch<{ entries: Calendar[] }>('/api/admin/business-calendars'),
  create: (body: unknown) => apiSend<{ id: string }>('/api/admin/business-calendars', 'POST', body),
  update: (id: string, body: unknown) => apiSend(`/api/admin/business-calendars/${id}`, 'PATCH', body),
  remove: (id: string) => apiSend(`/api/admin/business-calendars/${id}`, 'DELETE'),
};

const NINE_TO_FIVE: Shift[][] = [
  [],
  [{ start: '09:00', end: '17:00' }],
  [{ start: '09:00', end: '17:00' }],
  [{ start: '09:00', end: '17:00' }],
  [{ start: '09:00', end: '17:00' }],
  [{ start: '09:00', end: '17:00' }],
  [],
];

/** A blank calendar, Mon–Fri 09:00–17:00. */
const NEW_CALENDAR: Calendar = {
  id: '',
  name: '',
  description: null,
  timezone: 'Europe/London',
  week: NINE_TO_FIVE,
  holidays: [],
  isDefault: 0,
};

export function BusinessHoursPage() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.businessCalendars,
    queryFn: api.list,
  });
  /**
   * WHICH calendar is open is part of where you are, so it lives in the URL
   * (`?edit=<id>`, or `?edit=new`). Kai: *"there's not sub urls depending on
   * which one I go into"*. A refresh now keeps your place and the link is
   * shareable.
   *
   * The DRAFT stays local — an unsaved edit is not a location, and putting a
   * half-typed calendar in the address bar would be both unreadable and a
   * way to leak a draft into someone else's browser via a shared link.
   */
  const [editId, setEditId] = useUrlState('edit', null);
  const [draft, setDraft] = useState<Calendar | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: qk.businessCalendars });

  const openEditor = (cal: Calendar) => {
    setDraft(cal.id ? cal : { ...cal, week: cal.week.map((d) => d.map((x) => ({ ...x }))) });
    setEditId(cal.id || 'new');
    setFormError(null);
  };
  // Clear the guard FIRST: `editId` lives in the URL, so closing the editor
  // is a NAVIGATION, and the unsaved-changes blocker would otherwise fire on
  // Save (nothing unsaved) and on Cancel (discarding is what was asked for).
  const closeEditor = () => {
    clearDirty();
    setDraft(null);
    setEditId(null);
    setFormError(null);
  };

  const save = useMutation({
    mutationFn: (c: Calendar) =>
      c.id
        ? api.update(c.id, {
            name: c.name,
            description: c.description ?? undefined,
            timezone: c.timezone,
            week: c.week,
            holidays: c.holidays,
            isDefault: !!c.isDefault,
          })
        : api.create({
            name: c.name,
            description: c.description ?? undefined,
            timezone: c.timezone,
            week: c.week,
            holidays: c.holidays,
            isDefault: !!c.isDefault,
          }),
    onSuccess: (res) => {
      if (!res.ok) {
        setFormError(res.error ?? 'Could not save that calendar');
        return;
      }
      closeEditor();
      setFormError(null);
      invalidate();
    },
  });

  // Hooks run on EVERY render, so everything below sits ABOVE the loading and
  // error returns. Putting them after an early return is React error #310.
  const entries: Calendar[] = data?.entries ?? [];

  /**
   * The calendar being edited: the URL says WHICH, the server says what it
   * currently is, and `draft` holds unsaved changes on top.
   *
   * Seeding happens in an effect rather than at render so a slow list does
   * not lose an edit that arrived first (deep link, refresh).
   */
  const serverCal = editId && editId !== 'new' ? entries.find((x) => x.id === editId) : null;
  useEffect(() => {
    if (!editId) {
      setDraft(null);
      return;
    }
    if (editId === 'new') {
      setDraft((d) => d ?? { ...NEW_CALENDAR, week: NEW_CALENDAR.week.map((x) => x.map((y) => ({ ...y }))) });
      return;
    }
    const found = entries.find((x) => x.id === editId);
    // Only seed when switching records — re-seeding on every list refetch
    // would silently discard whatever is being typed.
    setDraft((d) => (d && d.id === editId ? d : (found ?? null)));
  }, [editId, entries]);

  const c = draft;

  /** Dirty against what the SERVER holds, not against the initial render —
   *  that is what makes Save light up only for a real change. */
  const dirty =
    !!c &&
    (editId === 'new'
      ? true
      : !serverCal || JSON.stringify({ ...c, id: '' }) !== JSON.stringify({ ...serverCal, id: '' }));
  const clearDirty = useDirty(dirty);

  if (isPending) return <Skeleton className="m-6 h-64 w-full max-w-4xl" />;
  if (isError)
    return (
      <div className="m-6 max-w-3xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load business hours</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-1 flex items-center gap-3">
        <CalendarClock className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Business hours</h1>
        <div className="flex-1" />
        <Button size="sm" onClick={() => openEditor(NEW_CALENDAR)}>
          <Plus /> New calendar
        </Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        SLA clocks only run inside these hours. A ticket raised at 17:30 on a Friday is not overdue at 21:30 —
        it is due when the desk next opens.
      </p>

      {c ? (
        <div className="space-y-5 rounded-lg border bg-card p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Name</label>
              <Input
                autoFocus
                value={c.name}
                onChange={(e) => setDraft({ ...c, name: e.target.value })}
                placeholder="Standard business hours"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Time zone</label>
              <Select value={c.timezone} onChange={(e) => setDraft({ ...c, timezone: e.target.value })}>
                {ZONES.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
                {!ZONES.includes(c.timezone) && <option value={c.timezone}>{c.timezone}</option>}
              </Select>
              {/* The zone is the calendar's own, not the viewer's — a desk in
                  London stays 09:00 London however you are travelling. */}
              <p className="mt-1 text-xs text-muted-foreground">
                Hours below are in this zone, and follow its daylight saving changes.
              </p>
            </div>
          </div>

          <div>
            {/* Audit gap: stored and returned by the API, never editable. */}
            <label className="mb-1 block text-sm font-medium">Description</label>
            <Input
              value={c.description ?? ''}
              onChange={(e) => setDraft({ ...c, description: e.target.value })}
              placeholder="Monday to Friday, 09:00–17:00"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Working week</label>
            <div className="space-y-1.5">
              {DISPLAY_ORDER.map((i) => {
                const day = DAYS[i]!;
                const shifts = c.week[i] ?? [];
                const open = shifts.length > 0;
                return (
                  <div key={day} className="flex flex-wrap items-center gap-2 rounded-md border p-2">
                    <label className="flex w-32 shrink-0 cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={open}
                        onChange={(e) => {
                          const week = c.week.map((d) => d.map((s) => ({ ...s })));
                          week[i] = e.target.checked ? [{ start: '09:00', end: '17:00' }] : [];
                          setDraft({ ...c, week });
                        }}
                        className="h-3.5 w-3.5 rounded border-input"
                      />
                      {day}
                    </label>
                    {open ? (
                      <div className="flex flex-wrap items-center gap-2">
                        {shifts.map((sh, si) => (
                          <span key={si} className="flex items-center gap-1.5">
                            <Input
                              type="time"
                              value={sh.start}
                              onChange={(e) => {
                                const week = c.week.map((d) => d.map((x) => ({ ...x })));
                                week[i]![si]!.start = e.target.value;
                                setDraft({ ...c, week });
                              }}
                              className="h-8 w-28 dark:[color-scheme:dark]"
                            />
                            <span className="text-xs text-muted-foreground">to</span>
                            <Input
                              type="time"
                              value={sh.end}
                              onChange={(e) => {
                                const week = c.week.map((d) => d.map((x) => ({ ...x })));
                                week[i]![si]!.end = e.target.value;
                                setDraft({ ...c, week });
                              }}
                              className="h-8 w-28 dark:[color-scheme:dark]"
                            />
                            {shifts.length > 1 && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const week = c.week.map((d) => d.map((x) => ({ ...x })));
                                  week[i]!.splice(si, 1);
                                  setDraft({ ...c, week });
                                }}
                                aria-label={`Remove shift from ${day}`}
                              >
                                <Trash2 />
                              </Button>
                            )}
                          </span>
                        ))}
                        {/* A second shift is how a lunch break is expressed —
                            09:00–12:00 and 13:00–17:00 means the hour between
                            is not SLA time. */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const week = c.week.map((d) => d.map((x) => ({ ...x })));
                            week[i]!.push({ start: '13:00', end: '17:00' });
                            setDraft({ ...c, week });
                          }}
                        >
                          <Plus /> Split shift
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Closed</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Holidays</label>
            <p className="mb-2 text-xs text-muted-foreground">Closed all day. Clocks skip these entirely.</p>
            <div className="flex flex-wrap items-center gap-2">
              {c.holidays.map((h) => (
                <Badge key={h} variant="outline" className="gap-1.5">
                  {h}
                  <button
                    onClick={() => setDraft({ ...c, holidays: c.holidays.filter((x) => x !== h) })}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Remove ${h}`}
                  >
                    ×
                  </button>
                </Badge>
              ))}
              <Input
                type="date"
                className="h-8 w-40 dark:[color-scheme:dark]"
                onChange={(e) => {
                  const v = e.target.value;
                  if (v && !c.holidays.includes(v)) setDraft({ ...c, holidays: [...c.holidays, v].sort() });
                  e.target.value = '';
                }}
                aria-label="Add a holiday"
              />
            </div>
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!c.isDefault}
              onChange={(e) => setDraft({ ...c, isDefault: e.target.checked ? 1 : 0 })}
              className="h-3.5 w-3.5 rounded border-input"
            />
            Use this calendar when a policy does not name one
          </label>

          {formError && <p className="text-sm text-destructive">{formError}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => closeEditor()}>
              Cancel
            </Button>
            <Button disabled={!dirty || !c.name.trim() || save.isPending} onClick={() => save.mutate(c)}>
              Save
            </Button>
          </div>
        </div>
      ) : data!.entries.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
          No calendars yet.
        </div>
      ) : (
        <div className="space-y-3">
          {data!.entries.map((cal) => (
            <div key={cal.id} className="rounded-lg border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{cal.name}</span>
                {!!cal.isDefault && <Badge variant="outline">Default</Badge>}
                <span className="text-xs text-muted-foreground">{cal.timezone}</span>
                <div className="flex-1" />
                <Button size="sm" variant="outline" onClick={() => openEditor(cal)}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-destructive/40 text-destructive hover:bg-destructive/10"
                  onClick={async () => {
                    if (
                      !(await confirm({
                        title: `Delete “${cal.name}”?`,
                        destructive: true,
                        confirmLabel: 'Delete',
                      }))
                    )
                      return;
                    const res = await api.remove(cal.id);
                    if (!res.ok) toast.error(res.error ?? 'Could not delete');
                    invalidate();
                  }}
                  aria-label={`Delete ${cal.name}`}
                >
                  <Trash2 />
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {DISPLAY_ORDER.map((i) =>
                  (cal.week[i] ?? []).length
                    ? `${DAYS[i]!.slice(0, 3)} ${(cal.week[i] ?? [])
                        .map((s) => `${s.start}–${s.end}`)
                        .join(', ')}`
                    : null,
                )
                  .filter(Boolean)
                  .join(' · ') || 'Never open'}
                {cal.holidays.length > 0 && ` · ${cal.holidays.length} holiday(s)`}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
